import React, { useEffect, useState, useCallback } from 'react';
import { MainLayout } from '@/components/Layout';
import { products, categories as categoriesApi, uploads, stock, combos as combosApi } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, Package, Calculator, Copy, List, ChevronDown, ChevronUp, Barcode, Tag, Search, Upload, X, Image as ImageIcon, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { ProductOptionsManager } from '@/components/ProductOptionsManager';
import { IfoodProductConfig } from '@/components/ifood/IfoodProductConfig';
import { CardapioSyncButtons } from '@/components/ifood/CardapioSyncButtons';

const UNITS = [
  { value: 'UN', label: 'UN - UNIDADE' },
  { value: 'KG', label: 'KG - KILOGRAMA' },
  { value: 'L', label: 'L - LITRO' },
  { value: 'CX', label: 'CX - CAIXA' },
  { value: 'PCT', label: 'PCT - PACOTE' },
  { value: 'FD', label: 'FD - FARDO' },
];

const PRODUCTION_AREAS = [
  { value: 'COZINHA', label: 'Cozinha' },
  { value: 'LANCHE', label: 'Lanche' },
  { value: 'BAR', label: 'Bar' },
];

// Modal de Calculadora de Preço de Venda
function PriceCalculatorModal({ open, onOpenChange, purchasePrice, onConfirm }) {
  const [preco_compra, setPrecoCompra] = useState(purchasePrice || 0);
  const [adicional_custo, setAdicionalCusto] = useState(0);
  const [descontos, setDescontos] = useState(0);
  const [margem_desejada, setMargemDesejada] = useState(100);
  
  useEffect(() => {
    setPrecoCompra(purchasePrice || 0);
  }, [purchasePrice, open]);
  
  const custo_total = parseFloat(preco_compra) + parseFloat(adicional_custo) - parseFloat(descontos);
  const markup = margem_desejada > 0 ? (1 + margem_desejada / 100) : 1;
  const preco_venda_sugerido = custo_total * markup;
  const margem_atual = custo_total > 0 ? ((preco_venda_sugerido - custo_total) / custo_total) * 100 : 0;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(preco_venda_sugerido.toFixed(2));
    toast.success('Preço copiado!');
  };
  
  const handleConfirm = () => {
    onConfirm(preco_venda_sugerido.toFixed(2));
    onOpenChange(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Calcular preço de venda</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 items-center">
            <Label>Preço de Compra</Label>
            <Input
              type="number"
              step="0.01"
              value={preco_compra}
              onChange={(e) => setPrecoCompra(e.target.value)}
              className="text-right bg-white border-slate-300"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4 items-center">
            <div>
              <Label className="text-emerald-600">(+) adicional de custo</Label>
              <span className="text-xs text-slate-500 block">ex: frete, seguro e impostos</span>
            </div>
            <Input
              type="number"
              step="0.01"
              value={adicional_custo}
              onChange={(e) => setAdicionalCusto(e.target.value)}
              className="text-right bg-white border-slate-300"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4 items-center">
            <Label className="text-red-600">(-) descontos</Label>
            <Input
              type="number"
              step="0.01"
              value={descontos}
              onChange={(e) => setDescontos(e.target.value)}
              className="text-right bg-white border-slate-300"
            />
          </div>
          
          <div className="border-t pt-4">
            <div className="grid grid-cols-2 gap-4 items-center mb-3">
              <Label>% Margem atual (calculada)</Label>
              <Input
                type="text"
                value={margem_atual.toFixed(2)}
                readOnly
                className="text-right bg-slate-100 border-slate-300"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4 items-center mb-3">
              <Label>Mark Up</Label>
              <Input
                type="text"
                value={markup.toFixed(2)}
                readOnly
                className="text-right bg-slate-100 border-slate-300"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4 items-center mb-3">
              <div className="flex items-center gap-2">
                <Label>% Margem desejada</Label>
                <Button type="button" size="sm" variant="outline" className="text-xs h-7 border-slate-300">
                  calc. margem
                </Button>
              </div>
              <Input
                type="number"
                step="0.01"
                value={margem_desejada}
                onChange={(e) => setMargemDesejada(e.target.value)}
                className="text-right bg-white border-slate-300"
              />
            </div>
          </div>
          
          <div className="border-t pt-4">
            <div className="grid grid-cols-2 gap-4 items-center">
              <Label className="font-bold">= Preço de venda sugerido</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={preco_venda_sugerido.toFixed(2)}
                  readOnly
                  className="text-right font-bold text-lg bg-blue-50 border-blue-300"
                />
                <Button type="button" size="icon" variant="outline" onClick={handleCopy} title="Copiar" className="border-slate-300">
                  <Copy size={16} />
                </Button>
              </div>
            </div>
          </div>
          
          <Button onClick={handleConfirm} className="w-full bg-slate-700 hover:bg-slate-800">
            ✓ Confirmar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Componente de Seção Expansível
function ExpandableSection({ title, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-sm font-medium text-slate-700">{title}</span>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {isOpen && <div className="p-4 border-t border-slate-200">{children}</div>}
    </div>
  );
}

// ====== IMAGE UPLOAD TAB ======
function ImageUploadTab({ imageUrl, onImageChange }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Selecione uma imagem'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Imagem muito grande (max 5MB)'); return; }
    setUploading(true);
    try {
      const res = await uploads.image(file);
      onImageChange(res.data.url);
      toast.success('Imagem enviada!');
    } catch { toast.error('Erro ao enviar imagem'); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-4" data-testid="image-upload-tab">
      {imageUrl ? (
        <div className="relative inline-block">
          <img src={imageUrl} alt="Produto" className="max-w-xs rounded-lg border border-slate-200 shadow-sm" data-testid="product-image-preview" />
          <button type="button" onClick={() => onImageChange('')} data-testid="remove-image-btn"
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
            <X size={14} />
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center cursor-pointer hover:border-[#FF6B6B] hover:bg-red-50/30 transition-colors"
          data-testid="image-upload-area"
        >
          {uploading ? (
            <p className="text-slate-500">Enviando...</p>
          ) : (
            <>
              <Upload size={48} className="mx-auto mb-4 text-slate-400" />
              <p className="text-slate-600 font-medium">Clique para selecionar uma imagem</p>
              <p className="text-sm text-slate-400 mt-1">JPG, PNG, WEBP (max 5MB)</p>
            </>
          )}
        </div>
      )}
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} data-testid="image-file-input" />
      {!imageUrl && (
        <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="border-slate-300" data-testid="select-image-btn">
          <ImageIcon size={16} className="mr-2" /> Selecionar Imagem
        </Button>
      )}
    </div>
  );
}

// ====== COMPOSITION TAB ======
function CompositionTab({ productId }) {
  const [recipe, setRecipe] = useState(null);
  const [stockItems, setStockItems] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newIngredient, setNewIngredient] = useState({ stock_item_id: '', qty: '', unit: 'KG' });
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [recipesRes, itemsRes] = await Promise.all([
        stock.recipes.list(),
        stock.items.list()
      ]);
      const productRecipe = recipesRes.data.find(r => r.product_id === productId);
      setRecipe(productRecipe || null);
      setStockItems(itemsRes.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [productId]);

  useEffect(() => { if (productId) loadData(); else setLoading(false); }, [productId, loadData]);

  const handleAddIngredient = async () => {
    if (!newIngredient.stock_item_id || !newIngredient.qty) { toast.error('Preencha todos os campos'); return; }
    try {
      // Encontrar o item de estoque para pegar a base_unit
      const selectedItem = stockItems.find(i => i.id === newIngredient.stock_item_id);
      const baseUnit = selectedItem?.base_unit || newIngredient.unit;
      
      if (recipe) {
        const updatedIngredients = [...(recipe.ingredients || []).map(i => ({
          stock_item_id: i.stock_item_id,
          qty_base: i.qty_base || i.qty,
          waste_factor: i.waste_factor || 0
        })), {
          stock_item_id: newIngredient.stock_item_id,
          qty_base: parseFloat(newIngredient.qty),
          waste_factor: 0
        }];
        await stock.recipes.update(recipe.id, {
          product_id: productId,
          yield_qty: recipe.yield_qty,
          yield_unit: recipe.yield_unit,
          ingredients: updatedIngredients
        });
      } else {
        await stock.recipes.create({
          product_id: productId,
          yield_qty: 1,
          yield_unit: 'UN',
          ingredients: [{ stock_item_id: newIngredient.stock_item_id, qty_base: parseFloat(newIngredient.qty), waste_factor: 0 }]
        });
      }
      toast.success('Insumo adicionado!');
      setShowAddForm(false);
      setNewIngredient({ stock_item_id: '', qty: '', unit: 'KG' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao adicionar insumo'); }
  };

  if (!productId) {
    return (
      <div className="text-center py-8 text-slate-500" data-testid="composition-save-first">
        <Package size={40} className="mx-auto mb-3 opacity-50" />
        <p>Salve o produto primeiro para adicionar a composicao</p>
      </div>
    );
  }

  if (loading) return <div className="text-center py-8 text-slate-500">Carregando...</div>;

  const ingredients = recipe?.ingredients?.map(ing => ({
    ...ing,
    qty: ing.qty_base || ing.qty,
    unit: ing.base_unit || ing.unit,
    cost_unit: ing.unit_cost || ing.cost_unit || 0,
    cost_total: ing.total_cost || ing.cost_total || 0
  })) || [];
  const totalCost = ingredients.reduce((sum, i) => sum + (i.cost_total || 0), 0);

  return (
    <div className="space-y-4" data-testid="composition-tab">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold text-slate-700">Ficha Tecnica / Composicao</h3>
          {totalCost > 0 && <p className="text-sm text-slate-500">Custo total: {formatCurrency(totalCost)}</p>}
        </div>
        <Button type="button" variant="outline" className="border-slate-300" onClick={() => setShowAddForm(!showAddForm)} data-testid="add-ingredient-btn">
          <Plus size={16} className="mr-2" /> Adicionar Insumo
        </Button>
      </div>

      {showAddForm && (
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-3" data-testid="ingredient-form">
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-2">
              <Label className="text-sm">Insumo</Label>
              <select value={newIngredient.stock_item_id} onChange={e => setNewIngredient(s => ({ ...s, stock_item_id: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" data-testid="ingredient-item-select">
                <option value="">Selecione um insumo</option>
                {stockItems.map(item => (
                  <option key={item.id} value={item.id}>{item.name} ({item.base_unit})</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm">Quantidade</Label>
              <Input type="number" step="0.001" value={newIngredient.qty} onChange={e => setNewIngredient(s => ({ ...s, qty: e.target.value }))}
                className="bg-white border-slate-300 mt-1" placeholder="0.000" data-testid="ingredient-qty" />
            </div>
            <div>
              <Label className="text-sm">Unidade</Label>
              <select value={newIngredient.unit} onChange={e => setNewIngredient(s => ({ ...s, unit: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" data-testid="ingredient-unit-select">
                <option value="KG">KG</option><option value="G">G</option><option value="L">L</option>
                <option value="ML">ML</option><option value="UN">UN</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" onClick={handleAddIngredient} className="bg-emerald-600 hover:bg-emerald-700 text-sm" data-testid="confirm-ingredient-btn">Confirmar</Button>
            <Button type="button" variant="outline" onClick={() => setShowAddForm(false)} className="text-sm">Cancelar</Button>
          </div>
        </div>
      )}

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Insumo</TableHead>
              <TableHead>Quantidade</TableHead>
              <TableHead>Unidade</TableHead>
              <TableHead className="text-right">Custo Unit.</TableHead>
              <TableHead className="text-right">Custo Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ingredients.length > 0 ? ingredients.map((ing, i) => (
              <TableRow key={i} data-testid={`ingredient-row-${i}`}>
                <TableCell className="font-medium">{ing.stock_item_name || ing.stock_item_id}</TableCell>
                <TableCell>{ing.qty}</TableCell>
                <TableCell>{ing.unit}</TableCell>
                <TableCell className="text-right">{formatCurrency(ing.cost_unit || 0)}</TableCell>
                <TableCell className="text-right font-medium">{formatCurrency(ing.cost_total || 0)}</TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                  Nenhum insumo na composicao. Clique em "Adicionar Insumo" para comecar.
                </TableCell>
              </TableRow>
            )}
            {totalCost > 0 && (
              <TableRow className="bg-slate-50 font-semibold">
                <TableCell colSpan={4} className="text-right">Custo Total da Composicao:</TableCell>
                <TableCell className="text-right text-emerald-700">{formatCurrency(totalCost)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ====== STOCK TAB ======
function StockTab({ productId, productName }) {
  const [balances, setBalances] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) { setLoading(false); return; }
    const load = async () => {
      try {
        const [balRes, movRes] = await Promise.all([
          stock.balances.list({ stock_item_id: productId }),
          stock.movements.list({ limit: 10 })
        ]);
        setBalances(balRes.data || []);
        setMovements((movRes.data || []).slice(0, 5));
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [productId]);

  if (!productId) {
    return (
      <div className="text-center py-8 text-slate-500" data-testid="stock-save-first">
        <Package size={40} className="mx-auto mb-3 opacity-50" />
        <p>Salve o produto primeiro para visualizar o estoque</p>
      </div>
    );
  }

  if (loading) return <div className="text-center py-8 text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-4" data-testid="stock-product-tab">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <p className="text-sm text-emerald-700 font-medium">Modulo de Estoque Ativo</p>
        <p className="text-xs text-emerald-600 mt-1">
          O controle de estoque esta integrado com a composicao do produto para calculo automatico de CMV e baixa por venda.
          Gerencie o estoque detalhado na pagina <a href="/stock" className="underline font-medium">Estoque</a>.
        </p>
      </div>

      {balances.length > 0 ? (
        <div>
          <h4 className="font-medium text-slate-700 mb-2">Saldos em Estoque</h4>
          <div className="grid grid-cols-3 gap-4">
            {balances.map((b, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`stock-balance-${i}`}>
                <p className="text-sm text-slate-500">{b.location_name || 'Local'}</p>
                <p className="text-2xl font-bold text-slate-800">{b.qty} {b.unit || 'UN'}</p>
                <p className="text-xs text-slate-400 mt-1">Custo medio: {formatCurrency(b.avg_cost || 0)}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg p-6 text-center">
          <Package size={32} className="mx-auto mb-2 text-slate-300" />
          <p className="text-slate-500">Nenhum saldo de estoque encontrado para este produto.</p>
          <p className="text-xs text-slate-400 mt-1">Cadastre itens de estoque e vincule via composicao ou na pagina de Estoque.</p>
        </div>
      )}

      {movements.length > 0 && (
        <div>
          <h4 className="font-medium text-slate-700 mb-2">Ultimas Movimentacoes</h4>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Tipo</TableHead>
                  <TableHead>Qtd</TableHead>
                  <TableHead>Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((m, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <span className={cn("px-2 py-0.5 rounded text-xs font-medium",
                        m.movement_type === 'ENTRADA' ? 'bg-emerald-100 text-emerald-700' :
                        m.movement_type === 'SAIDA' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                      )}>{m.movement_type}</span>
                    </TableCell>
                    <TableCell>{m.qty}</TableCell>
                    <TableCell>{new Date(m.created_at).toLocaleDateString('pt-BR')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

export function ProductsPage() {
  const [productsList, setProductsList] = useState([]);
  const [syncStatusMap, setSyncStatusMap] = useState({});

  // Renderiza chip de sincronização da plataforma
  const SyncChip = ({ status, platform }) => {
    if (!status || !status.synced) {
      return (
        <span
          className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500"
          title={`${platform}: não sincronizado`}
          data-testid={`sync-${platform.toLowerCase()}-none`}
        >
          —
        </span>
      );
    }
    const isAvailable = (status.status || '').toUpperCase() === 'AVAILABLE';
    return (
      <span
        className={cn(
          "px-2 py-0.5 rounded text-[10px] font-bold inline-flex items-center gap-1",
          isAvailable ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"
        )}
        title={`${platform}: ${isAvailable ? 'ativo' : 'inativo / pausado'}`}
        data-testid={`sync-${platform.toLowerCase()}-${isAvailable ? 'available' : 'unavailable'}`}
      >
        <span className={cn("w-1.5 h-1.5 rounded-full", isAvailable ? "bg-emerald-500" : "bg-amber-500")} />
        {isAvailable ? 'Ativo' : 'Pausado'}
      </span>
    );
  };
  const [combosList, setCombosList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'form'
  const [editingProduct, setEditingProduct] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('TODOS');
  const [filterType, setFilterType] = useState('TODOS'); // 'TODOS', 'PRODUTOS', 'COMBOS'
  
  // Modal de Combo
  const [showComboModal, setShowComboModal] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null);
  const [comboForm, setComboForm] = useState({
    name: '',
    description: '',
    category_id: '',
    price: '',
    items: []
  });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    unit: 'UN',
    barcode: '',
    ncm: '',
    purchase_price: '',
    price: '',
    price_99food: '',
    price_digital: '',
    product_type: 'PRODUZIDO',
    production_area: 'COZINHA',
    prep_time_minutes: '15',
    is_active: true,
    notes: '',
    always_ask_notes: false,
    image_url: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        products.list(),
        categoriesApi.list()
      ]);
      setProductsList(productsRes.data);
      setCategoriesList(categoriesRes.data);

      // Carregar status de sincronização iFood/99Food em paralelo (não-bloqueante)
      try {
        const syncRes = await products.syncStatus();
        setSyncStatusMap(syncRes.data?.items || {});
      } catch {
        setSyncStatusMap({});
      }
      
      // Carregar combos separadamente para não quebrar se não existir
      try {
        const combosRes = await combosApi.list();
        setCombosList(combosRes.data || []);
      } catch {
        // Tabela de combos pode não existir ainda
        setCombosList([]);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category_id: '',
      unit: 'UN',
      barcode: '',
      ncm: '',
      purchase_price: '',
      price: '',
      price_99food: '',
      price_digital: '',
      product_type: 'PRODUZIDO',
      production_area: 'COZINHA',
      prep_time_minutes: '15',
      is_active: true,
      notes: '',
      always_ask_notes: false,
      image_url: '',
    });
    setEditingProduct(null);
  };

  const handleNewProduct = () => {
    resetForm();
    setViewMode('form');
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      category_id: product.category_id || '',
      unit: 'UN',
      barcode: product.barcode || product.sku || '',
      ncm: product.ncm || '',
      purchase_price: product.cost_override?.toString() || '',
      price: product.price?.toString() || '',
      product_type: product.product_type || 'PRODUZIDO',
      production_area: product.production_area || 'COZINHA',
      prep_time_minutes: product.prep_time_minutes?.toString() || '15',
      is_active: product.is_active !== false,
      notes: '',
      always_ask_notes: false,
      image_url: product.image_url || '',
      price_99food: product.price_99food?.toString() || '',
      price_digital: product.price_digital?.toString() || '',
    });
    setViewMode('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        name: formData.name,
        description: formData.description,
        category_id: formData.category_id,
        price: parseFloat(formData.price) || 0,
        cost_override: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        price_99food: formData.price_99food ? parseFloat(formData.price_99food) : null,
        price_digital: formData.price_digital ? parseFloat(formData.price_digital) : null,
        product_type: formData.product_type,
        production_area: formData.production_area,
        prep_time_minutes: parseInt(formData.prep_time_minutes) || 15,
        sku: formData.barcode || null,
        barcode: formData.barcode || null,
        image_url: formData.image_url || null,
        ncm: formData.ncm || null,
        is_active: formData.is_active,
      };

      console.log('=== SALVANDO PRODUTO ===');
      console.log('formData.purchase_price:', formData.purchase_price);
      console.log('data.cost_override:', data.cost_override);
      console.log('data completo:', JSON.stringify(data, null, 2));

      if (editingProduct) {
        const response = await products.update(editingProduct.id, data);
        console.log('Resposta update:', response);
        toast.success('Produto atualizado!');
      } else {
        const response = await products.create(data);
        console.log('Resposta create:', response);
        toast.success('Produto cadastrado!');
      }
      
      setViewMode('list');
      resetForm();
      loadData();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar produto');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este produto?')) return;
    
    try {
      await products.delete(id);
      toast.success('Produto excluído!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const res = await products.toggleActive(id);
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error('Erro ao alterar status do produto');
    }
  };

  const handlePriceCalculated = (price) => {
    setFormData({ ...formData, price });
  };

  const generateBarcode = () => {
    const code = Math.floor(Math.random() * 9000000000000) + 1000000000000;
    setFormData({ ...formData, barcode: code.toString() });
  };

  // ========== FUNÇÕES DE COMBO ==========
  const resetComboForm = () => {
    setComboForm({ name: '', description: '', category_id: '', price: '', items: [] });
    setEditingCombo(null);
  };

  const handleNewCombo = () => {
    resetComboForm();
    setShowComboModal(true);
  };

  const handleEditCombo = (combo) => {
    setEditingCombo(combo);
    setComboForm({
      name: combo.name,
      description: combo.description || '',
      category_id: combo.category_id,
      price: combo.price.toString(),
      items: combo.items.map(i => ({
        child_product_id: i.child_product_id,
        child_product_name: i.child_product_name,
        quantity: i.quantity,
        sort_order: i.sort_order
      }))
    });
    setShowComboModal(true);
  };

  const handleComboSubmit = async (e) => {
    e.preventDefault();
    if (!comboForm.name || !comboForm.category_id || !comboForm.price) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (comboForm.items.length < 2) {
      toast.error('Um combo deve ter pelo menos 2 produtos');
      return;
    }

    try {
      const payload = {
        name: comboForm.name,
        description: comboForm.description,
        category_id: comboForm.category_id,
        price: parseFloat(comboForm.price),
        items: comboForm.items.map((item, idx) => ({
          child_product_id: item.child_product_id,
          quantity: item.quantity,
          sort_order: idx
        }))
      };

      if (editingCombo) {
        await combosApi.update(editingCombo.id, payload);
        toast.success('Combo atualizado!');
      } else {
        await combosApi.create(payload);
        toast.success('Combo criado!');
      }
      
      setShowComboModal(false);
      resetComboForm();
      loadData();
    } catch (err) {
      console.error('Erro combo:', err);
      toast.error('Erro ao salvar combo. Verifique se o módulo de combos está ativo.');
    }
  };

  const handleDeleteCombo = async (id) => {
    if (!window.confirm('Deseja realmente excluir este combo?')) return;
    try {
      await combosApi.delete(id);
      toast.success('Combo excluído!');
      loadData();
    } catch (err) {
      console.error('Erro ao excluir combo:', err);
      toast.error('Erro ao excluir combo. Verifique se o módulo de combos está ativo.');
    }
  };

  const addProductToCombo = (productId) => {
    const product = productsList.find(p => p.id === productId);
    if (!product) return;
    if (comboForm.items.find(i => i.child_product_id === productId)) {
      toast.error('Produto já está no combo');
      return;
    }
    setComboForm({
      ...comboForm,
      items: [...comboForm.items, {
        child_product_id: productId,
        child_product_name: product.name,
        quantity: 1,
        sort_order: comboForm.items.length
      }]
    });
  };

  const removeProductFromCombo = (productId) => {
    setComboForm({
      ...comboForm,
      items: comboForm.items.filter(i => i.child_product_id !== productId)
    });
  };

  const updateComboItemQuantity = (productId, qty) => {
    setComboForm({
      ...comboForm,
      items: comboForm.items.map(i => 
        i.child_product_id === productId ? { ...i, quantity: parseInt(qty) || 1 } : i
      )
    });
  };

  // Calcular custo e margem estimados do combo
  const comboEstimates = (() => {
    let totalCost = 0;
    comboForm.items.forEach(item => {
      const product = productsList.find(p => p.id === item.child_product_id);
      if (product) {
        totalCost += (product.cost_override || product.price * 0.35) * item.quantity;
      }
    });
    const price = parseFloat(comboForm.price) || 0;
    const margin = price > 0 ? ((price - totalCost) / price * 100) : 0;
    return { totalCost, margin };
  })();

  const filteredProducts = productsList.filter(p => {
    if (searchTerm && !p.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterCategory !== 'TODOS' && p.category_id !== filterCategory) return false;
    if (filterType === 'COMBOS') return false; // Combos são mostrados separadamente
    if (filterType === 'PRODUTOS' && p.product_type === 'COMBO') return false;
    return true;
  });

  // Combos filtrados
  const filteredCombos = combosList.filter(c => {
    if (searchTerm && !c.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterCategory !== 'TODOS' && c.category_id !== filterCategory) return false;
    return true;
  });

  // Lista combinada para exibição
  const displayItems = filterType === 'COMBOS' 
    ? filteredCombos.map(c => ({ ...c, isCombo: true }))
    : filterType === 'PRODUTOS'
    ? filteredProducts.map(p => ({ ...p, isCombo: false }))
    : [
        ...filteredProducts.map(p => ({ ...p, isCombo: false })),
        ...filteredCombos.map(c => ({ ...c, isCombo: true }))
      ];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Carregando...</div>
        </div>
      </MainLayout>
    );
  }

  // MODO FORMULÁRIO (Cadastro/Edição)
  if (viewMode === 'form') {
    return (
      <MainLayout>
        <div className="space-y-6 page-enter" data-testid="products-form">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow Condensed' }}>
                Produtos
              </h1>
              <p className="text-slate-600">Cadastro de produto</p>
            </div>
            <div className="flex gap-2 sm:gap-3 flex-wrap">
              <CardapioSyncButtons />
              <Button variant="outline" onClick={() => setViewMode('list')} className="border-slate-300">
                <List size={18} className="mr-2" />
                Listagem
              </Button>
              <Button className="bg-[#FF6B6B] hover:bg-[#FF5252]" onClick={handleNewProduct}>
                <Plus size={18} className="mr-2" />
                Novo
              </Button>
            </div>
          </div>

          {/* Formulário com Abas */}
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="dados-principais" className="bg-white rounded-xl shadow-sm border border-slate-200">
              <TabsList className="w-full justify-start border-b border-slate-200 rounded-none bg-slate-50 p-0 h-auto flex-wrap">
                <TabsTrigger value="dados-principais" className="rounded-none border-b-2 border-transparent text-slate-600 data-[state=active]:border-[#FF6B6B] data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-none px-6 py-3 text-sm font-medium hover:text-slate-900">
                  Dados principais
                </TabsTrigger>
                <TabsTrigger value="dados-complementares" className="rounded-none border-b-2 border-transparent text-slate-600 data-[state=active]:border-[#FF6B6B] data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-none px-6 py-3 text-sm font-medium hover:text-slate-900">
                  Dados complementares
                </TabsTrigger>
                <TabsTrigger value="tributacoes" className="rounded-none border-b-2 border-transparent text-slate-600 data-[state=active]:border-[#FF6B6B] data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-none px-6 py-3 text-sm font-medium hover:text-slate-900">
                  Tributações (fiscais)
                </TabsTrigger>
                <TabsTrigger value="imagens" className="rounded-none border-b-2 border-transparent text-slate-600 data-[state=active]:border-[#FF6B6B] data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-none px-6 py-3 text-sm font-medium hover:text-slate-900">
                  Imagens
                </TabsTrigger>
                <TabsTrigger value="composicao" className="rounded-none border-b-2 border-transparent text-slate-600 data-[state=active]:border-[#FF6B6B] data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-none px-6 py-3 text-sm font-medium hover:text-slate-900">
                  Composição
                </TabsTrigger>
                <TabsTrigger value="estoque" className="rounded-none border-b-2 border-transparent text-slate-600 data-[state=active]:border-[#FF6B6B] data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-none px-6 py-3 text-sm font-medium hover:text-slate-900">
                  Estoque na empresa
                </TabsTrigger>
                <TabsTrigger value="variacoes" className="rounded-none border-b-2 border-transparent text-slate-600 data-[state=active]:border-[#FF6B6B] data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-none px-6 py-3 text-sm font-medium hover:text-slate-900">
                  Variações
                </TabsTrigger>
              </TabsList>

              {/* Aba Dados Principais */}
              <TabsContent value="dados-principais" className="p-6">
                <div className="grid grid-cols-3 gap-8">
                  {/* Coluna 1 - Campos principais */}
                  <div className="col-span-2 space-y-5">
                    {editingProduct && (
                      <div className="text-sm text-slate-500">
                        ID {editingProduct.id.substring(0, 8)}
                      </div>
                    )}

                    <div>
                      <Label className="text-slate-700">
                        Descrição <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        data-testid="product-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Nome do produto"
                        required
                        className="bg-white border-slate-300 mt-1"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-700 flex items-center gap-1">
                        <Plus size={14} className="text-[#FF6B6B]" />
                        Grupo <span className="text-red-500">*</span>
                      </Label>
                      <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                        <SelectTrigger className="bg-white border-slate-300 mt-1" data-testid="product-category-select">
                          <SelectValue placeholder="Selecione um grupo" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoriesList.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-slate-700">Unidade de medida</Label>
                      <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                        <SelectTrigger className="bg-white border-slate-300 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNITS.map((unit) => (
                            <SelectItem key={unit.value} value={unit.value}>
                              {unit.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-slate-700">
                        Código de barras <span className="text-red-500">*</span>
                      </Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          value={formData.barcode}
                          onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                          placeholder="EAN/GTIN"
                          className="bg-white border-slate-300"
                        />
                        <Button type="button" variant="outline" onClick={generateBarcode} className="whitespace-nowrap border-slate-300">
                          <Barcode size={16} className="mr-2" />
                          Gerar código sequencial
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-slate-700">
                        N.C.M. <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={formData.ncm}
                        onChange={(e) => setFormData({ ...formData, ncm: e.target.value })}
                        placeholder="00"
                        className="bg-white border-slate-300 mt-1"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-700">Área de Produção</Label>
                      <Select value={formData.production_area} onValueChange={(value) => setFormData({ ...formData, production_area: value })}>
                        <SelectTrigger className="bg-white border-slate-300 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRODUCTION_AREAS.map((area) => (
                            <SelectItem key={area.value} value={area.value}>
                              {area.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-slate-700">Tempo de Preparo (minutos)</Label>
                      <Input
                        type="number"
                        value={formData.prep_time_minutes}
                        onChange={(e) => setFormData({ ...formData, prep_time_minutes: e.target.value })}
                        className="bg-white border-slate-300 mt-1"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700">
                          Preço de compra <span className="text-red-500">*</span>
                        </Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.purchase_price}
                            onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                            className="bg-white border-slate-300"
                            data-testid="product-purchase-price"
                          />
                          <button type="button" className="text-xs text-[#FF6B6B] hover:underline whitespace-nowrap">
                            custo da composição
                          </button>
                          <button type="button" className="text-xs text-slate-500 hover:underline whitespace-nowrap">
                            Últimas compras
                          </button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label className="text-slate-700">
                        Preço de venda <span className="text-red-500">*</span>
                      </Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                          required
                          className="bg-white border-slate-300"
                          data-testid="product-price-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowCalculator(true)}
                          className="whitespace-nowrap border-slate-300"
                        >
                          <Calculator size={16} className="mr-2" />
                          Calcular preço de venda
                        </Button>
                      </div>
                    </div>

                    {/* Preços específicos por canal */}
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Preços por Canal</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-slate-700 flex items-center gap-1.5 mb-1">
                            <span className="inline-flex items-center justify-center bg-yellow-400 text-black text-[9px] font-black px-1.5 py-0.5 rounded">99</span>
                            Preço 99food
                          </Label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">R$</span>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Mesmo preço"
                              value={formData.price_99food}
                              onChange={(e) => setFormData({ ...formData, price_99food: e.target.value })}
                              className="bg-white border-yellow-300 focus:border-yellow-400 pl-7"
                              data-testid="product-price-99food"
                            />
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">Vazio = usa preço padrão</p>
                        </div>
                        <div>
                          <Label className="text-slate-700 flex items-center gap-1.5 mb-1">
                            <span className="inline-flex items-center justify-center bg-blue-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded">QR</span>
                            Cardápio Digital
                          </Label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">R$</span>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Mesmo preço"
                              value={formData.price_digital}
                              onChange={(e) => setFormData({ ...formData, price_digital: e.target.value })}
                              className="bg-white border-blue-300 focus:border-blue-400 pl-7"
                              data-testid="product-price-digital"
                            />
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">Vazio = usa preço padrão</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="is_active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      />
                      <Label htmlFor="is_active" className="text-slate-700 cursor-pointer">
                        Ativo
                      </Label>
                    </div>
                  </div>

                  {/* Coluna 2 - Observações e configurações */}
                  <div className="space-y-5">
                    <div>
                      <Label className="text-slate-700 flex items-center gap-1">
                        Observações para o produto
                        <span className="text-slate-400 cursor-help" title="Informações adicionais sobre o produto">ⓘ</span>
                      </Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="observação 1; observação 2; observação 3;"
                        rows={4}
                        className="bg-white border-slate-300 mt-1"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="always_ask_notes"
                        checked={formData.always_ask_notes}
                        onCheckedChange={(checked) => setFormData({ ...formData, always_ask_notes: checked })}
                      />
                      <Label htmlFor="always_ask_notes" className="text-slate-700 text-sm cursor-pointer">
                        Sempre solicitar observação no lançamento
                      </Label>
                    </div>

                    <ExpandableSection title="Mostrar dados fornecedor">
                      <div className="space-y-3">
                        <div>
                          <Label className="text-slate-600 text-sm">Fornecedor</Label>
                          <Select>
                            <SelectTrigger className="bg-white border-slate-300 mt-1">
                              <SelectValue placeholder="Selecione o fornecedor" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nenhum cadastrado</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-xs text-slate-500">
                          Preparado para integração com módulo de Estoque/CMV
                        </p>
                      </div>
                    </ExpandableSection>

                    <ExpandableSection title="Mostrar configurações do iFood">
                      <IfoodProductConfig productId={editingProduct?.id} />
                    </ExpandableSection>

                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-medium text-slate-700 mb-3">Franquia</h4>
                      <div>
                        <Label className="text-slate-600 text-sm"># Origem franquia</Label>
                        <Input 
                          placeholder="Código da franquia"
                          className="bg-white border-slate-300 mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Aba Dados Complementares */}
              <TabsContent value="dados-complementares" className="p-6">
                <div className="space-y-4">
                  <div>
                    <Label>Descrição detalhada</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className="bg-white border-slate-300 mt-1"
                      placeholder="Descrição completa do produto para cardápio digital e etiquetas"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tipo do Produto</Label>
                      <Select value={formData.product_type} onValueChange={(value) => setFormData({ ...formData, product_type: value })}>
                        <SelectTrigger className="bg-white border-slate-300 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="REVENDA">Revenda</SelectItem>
                          <SelectItem value="PRODUZIDO">Produzido</SelectItem>
                          <SelectItem value="SERVICO">Serviço</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Aba Tributações */}
              <TabsContent value="tributacoes" className="p-6">
                <div className="text-center py-12 text-slate-500">
                  <Tag size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Módulo de Tributações Fiscais</p>
                  <p className="text-sm">Em desenvolvimento - será integrado com emissão de NF-e/NFC-e</p>
                </div>
              </TabsContent>

              {/* Aba Imagens */}
              <TabsContent value="imagens" className="p-6">
                <ImageUploadTab
                  imageUrl={formData.image_url}
                  onImageChange={(url) => setFormData({ ...formData, image_url: url })}
                />
              </TabsContent>

              {/* Aba Composição */}
              <TabsContent value="composicao" className="p-6">
                <CompositionTab productId={editingProduct?.id} />
              </TabsContent>

              {/* Aba Estoque */}
              <TabsContent value="estoque" className="p-6">
                <StockTab productId={editingProduct?.id} productName={formData.name} />
              </TabsContent>

              {/* Aba Variações */}
              <TabsContent value="variacoes" className="p-6">
                <ProductOptionsManager productId={editingProduct?.id} productName={formData.name} />
              </TabsContent>
            </Tabs>

            {/* Botões de Ação */}
            <div className="flex items-center gap-4 mt-6 pt-4 border-t border-slate-200">
              <Button type="submit" className="bg-slate-700 hover:bg-slate-800" data-testid="save-product-btn">
                ✓ Salvar <span className="text-xs ml-1 opacity-70">(alt+s)</span>
              </Button>
              {editingProduct && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleDelete(editingProduct.id)}
                  className="text-red-600 hover:text-red-700 border-red-200"
                >
                  <Trash2 size={16} className="mr-2" />
                  Excluir
                </Button>
              )}
              <Button type="button" variant="outline" className="border-slate-300">
                <Tag size={16} className="mr-2" />
                Etiquetas
                <ChevronDown size={14} className="ml-2" />
              </Button>
            </div>
          </form>

          {/* Modal de Calculadora */}
          <PriceCalculatorModal
            open={showCalculator}
            onOpenChange={setShowCalculator}
            purchasePrice={formData.purchase_price}
            onConfirm={handlePriceCalculated}
          />
        </div>
      </MainLayout>
    );
  }

  // MODO LISTAGEM
  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="products-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow Condensed' }}>
              Produtos
            </h1>
            <p className="text-slate-600">Gerencie seu catálogo ({displayItems.length} itens)</p>
          </div>
          <div className="flex gap-2 sm:gap-3 flex-wrap">
            <CardapioSyncButtons />
            <Button variant="outline" className="border-slate-300" onClick={handleNewCombo} data-testid="new-combo-btn">
              <Layers size={18} className="mr-2" />
              Novo Combo
            </Button>
            <Button className="bg-[#FF6B6B] hover:bg-[#FF5252]" onClick={handleNewProduct} data-testid="new-product-btn">
              <Plus size={18} className="mr-2" />
              Novo
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <Input
                placeholder="Buscar produto ou combo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-slate-300"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40 bg-white border-slate-300">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                <SelectItem value="PRODUTOS">Produtos</SelectItem>
                <SelectItem value="COMBOS">Combos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-48 bg-white border-slate-300">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todas categorias</SelectItem>
                {categoriesList.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Tabela de Produtos e Combos */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-bold">Produto</TableHead>
                <TableHead className="font-bold">Tipo</TableHead>
                <TableHead className="font-bold">Grupo</TableHead>
                <TableHead className="font-bold text-right">Preço Venda</TableHead>
                <TableHead className="font-bold text-right">Preço 99F</TableHead>
                <TableHead className="font-bold text-center">iFood</TableHead>
                <TableHead className="font-bold text-center">99Food</TableHead>
                <TableHead className="font-bold text-center">Status</TableHead>
                <TableHead className="font-bold text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayItems.length > 0 ? (
                displayItems.map((item) => {
                  const category = categoriesList.find(c => c.id === item.category_id);
                  
                  if (item.isCombo) {
                    // Renderização de COMBO
                    return (
                      <TableRow key={`combo-${item.id}`} data-testid={`combo-row-${item.id}`} className="hover:bg-purple-50/50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                              <Layers className="text-white" size={20} />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{item.name}</p>
                              <p className="text-xs text-slate-500">
                                {item.items?.map(i => `${i.quantity}x ${i.child_product_name}`).join(' + ')}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-purple-100 text-purple-700">
                            COMBO
                          </span>
                        </TableCell>
                        <TableCell>{category?.name || '-'}</TableCell>
                        <TableCell className="text-right font-semibold text-lg">
                          {formatCurrency(item.price)}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500">—</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500" title="Combos não são publicados nas plataformas">—</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500" title="Combos não são publicados nas plataformas">—</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-emerald-100 text-emerald-700">
                            Ativo
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditCombo(item)}
                              title="Editar"
                              className="hover:bg-purple-100 hover:text-purple-700"
                            >
                              <Pencil size={18} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteCombo(item.id)}
                              title="Excluir"
                              className="hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 size={18} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  
                  // Renderização de PRODUTO normal
                  return (
                    <TableRow key={item.id} data-testid={`product-row-${item.id}`} className={cn("hover:bg-slate-50", item.is_active === false && "opacity-55")}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] flex items-center justify-center">
                            <Package className="text-white" size={20} />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{item.name}</p>
                            {item.sku && <p className="text-xs text-slate-500">SKU: {item.sku}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={cn(
                          "px-2 py-1 rounded text-xs font-semibold",
                          item.product_type === 'PRODUZIDO' ? 'bg-orange-100 text-orange-700' :
                          item.product_type === 'REVENDA' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        )}>
                          {item.product_type || 'PRODUZIDO'}
                        </span>
                      </TableCell>
                      <TableCell>{category?.name || '-'}</TableCell>
                      <TableCell className="text-right font-semibold text-lg">
                        {formatCurrency(item.price)}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.price_99food != null
                          ? <span className="font-semibold text-green-700">{formatCurrency(item.price_99food)}</span>
                          : <span className="text-slate-300 text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        <SyncChip status={syncStatusMap[item.id]?.ifood} platform="iFood" />
                      </TableCell>
                      <TableCell className="text-center">
                        <SyncChip status={syncStatusMap[item.id]?.food99} platform="99Food" />
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => handleToggleActive(item.id)}
                          title={item.is_active !== false ? 'Clique para inativar' : 'Clique para ativar'}
                          className={cn(
                            "px-2 py-1 rounded text-xs font-semibold cursor-pointer transition-opacity hover:opacity-70",
                            item.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                          )}
                        >
                          {item.is_active !== false ? 'Ativo' : 'Inativo'}
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(item)}
                            title="Editar"
                            className="hover:bg-slate-100 hover:text-slate-700"
                            data-testid={`edit-product-${item.id}`}
                          >
                            <Pencil size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(item.id)}
                            title="Excluir"
                            className="hover:bg-red-50 hover:text-red-600"
                            data-testid={`delete-product-${item.id}`}
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    {searchTerm || filterCategory !== 'TODOS' || filterType !== 'TODOS'
                      ? 'Nenhum item encontrado com esses critérios' 
                      : 'Nenhum produto cadastrado. Clique em "Novo" para começar.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Modal de Combo */}
        <Dialog open={showComboModal} onOpenChange={setShowComboModal}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers size={20} className="text-purple-600" />
                {editingCombo ? 'Editar Combo' : 'Novo Combo'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleComboSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome do Combo *</Label>
                  <Input 
                    value={comboForm.name} 
                    onChange={e => setComboForm({...comboForm, name: e.target.value})} 
                    placeholder="Ex: COMBO BRASA"
                    required 
                    className="bg-white border-slate-300 mt-1" 
                    data-testid="combo-name-input"
                  />
                </div>
                <div>
                  <Label>Preço de Venda *</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={comboForm.price} 
                    onChange={e => setComboForm({...comboForm, price: e.target.value})} 
                    placeholder="0.00"
                    required 
                    className="bg-white border-slate-300 mt-1" 
                    data-testid="combo-price-input"
                  />
                </div>
              </div>

              <div>
                <Label>Categoria *</Label>
                <Select value={comboForm.category_id} onValueChange={val => setComboForm({...comboForm, category_id: val})}>
                  <SelectTrigger className="bg-white border-slate-300 mt-1">
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesList.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Descrição</Label>
                <Input 
                  value={comboForm.description} 
                  onChange={e => setComboForm({...comboForm, description: e.target.value})} 
                  placeholder="Descrição opcional"
                  className="bg-white border-slate-300 mt-1" 
                />
              </div>

              {/* Produtos do Combo */}
              <div className="border border-purple-200 rounded-lg p-4 space-y-4 bg-purple-50/50">
                <div className="flex justify-between items-center">
                  <Label className="text-lg font-semibold text-purple-800">Produtos do Combo</Label>
                  <span className="text-sm text-purple-600">{comboForm.items.length} produtos</span>
                </div>

                {/* Adicionar produto */}
                <div>
                  <Label className="text-sm">Adicionar Produto</Label>
                  <Select onValueChange={addProductToCombo}>
                    <SelectTrigger className="bg-white border-slate-300 mt-1">
                      <SelectValue placeholder="Selecione um produto para adicionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {productsList
                        .filter(p => p.product_type !== 'COMBO' && !comboForm.items.find(i => i.child_product_id === p.id))
                        .map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} - {formatCurrency(p.price)}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Lista de produtos no combo */}
                {comboForm.items.length > 0 && (
                  <div className="space-y-2">
                    {comboForm.items.map((item, idx) => (
                      <div key={item.child_product_id} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-purple-100">
                        <span className="text-purple-400 font-mono text-sm">{idx + 1}.</span>
                        <span className="flex-1 font-medium">{item.child_product_name}</span>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs">Qtd:</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={e => updateComboItemQuantity(item.child_product_id, e.target.value)}
                            className="w-16 bg-white border-slate-300 text-center"
                          />
                        </div>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => removeProductFromCombo(item.child_product_id)}
                          className="hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {comboForm.items.length === 0 && (
                  <div className="text-center py-4 text-purple-400">
                    Adicione pelo menos 2 produtos ao combo
                  </div>
                )}
              </div>

              {/* Resumo */}
              {comboForm.items.length > 0 && comboForm.price && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Custo Estimado:</span>
                    <span className="font-semibold">{formatCurrency(comboEstimates.totalCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Margem Estimada:</span>
                    <span className={cn("font-bold", 
                      comboEstimates.margin >= 50 ? 'text-emerald-600' : 
                      comboEstimates.margin >= 30 ? 'text-blue-600' : 'text-red-600')}>
                      {comboEstimates.margin.toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowComboModal(false)}>Cancelar</Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="save-combo-btn">
                  {editingCombo ? 'Atualizar Combo' : 'Criar Combo'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
